import { useCreateFileRecord } from "@/api/fileRecords";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";

export interface NewFolderDialogProps {
  onClose: () => void;
  workspaceId: string;
  currentPath: string;
  onSuccess?: () => void;
}

export function NewFolderDialog({
  onClose,
  workspaceId,
  currentPath,
  onSuccess,
}: NewFolderDialogProps) {
  const [name, setName] = useState("");

  useEffect(() => {
    setName("");
  }, []);

  const createFileRecord = useCreateFileRecord(workspaceId, {
    onSuccess: () => {
      toast.success("Folder created");
      onSuccess?.();
      onClose();
    },
    onError: () => toast.error("Failed to create folder"),
  });

  const handleSubmit = () => {
    const trimmed = name.trim();
    if (!trimmed) return;

    const folderPath = currentPath
      ? `${currentPath}${trimmed}/`
      : `${trimmed}/`;

    createFileRecord.mutate({
      provider: "s3",
      destination: {
        class: "workspace",
        workspace_id: workspaceId,
        path: folderPath,
        is_folder: true,
      },
    });
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New Folder</DialogTitle>
          <DialogDescription>
            Enter a name for the new folder.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <Input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Folder name"
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSubmit();
            }}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!name.trim() || createFileRecord.isPending}
          >
            {createFileRecord.isPending ? "Creating..." : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
